import { setLocale } from 'yup'
import { ru } from 'yup-locales'
import { PageLink, PageTitle } from '../../../../../../../_metronic/layout/core'
import { KTIcon } from '../../../../../../../_metronic/helpers'
import { LoaderForTable } from '../../../../../../../components/Loader'
import ReactPaginate from 'react-paginate'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { serializeFiltersToUrl } from '../../../../../../../utils/statusFiltersURL'
import { ROLE, useAuth } from '../../../../../auth'
import ProviderSummary from '../common/ProviderSummary'
import ProviderList from './ProviderList'
import ModalProvider from '../common/ModalProvider'
import { getMerchantLookup, getPaymentMethodLookup, getProviderBindings, getProviderList, getProviderManagementSummary, toggleProviderEnabled } from '../../core/api'
import { DEFAULT_PROVIDER_LIST_QUERY } from '../../core/query.defaults'
import { PROVIDER_MANAGEMENT_BREADCRUMBS } from '../../core/consts'
import { ProviderListQuery } from '../../core/query.types'
import { ApplyProviderFiltersArgs, IProviderBase, IProviderDetails, IProviderFormMeta, IProviderSummary } from '../../core/types'

setLocale(ru)

export default () => {
  const location = useLocation()
  const { currentUser } = useAuth()
  const canUpdateKeys = useMemo(
    () => currentUser?.roles?.some((r) => r === ROLE.ROLE_ADMIN || r === ROLE.ROLE_MANAGER) ?? false,
    [currentUser],
  )
  const breadCrumbs = useMemo<PageLink[]>(
    () => PROVIDER_MANAGEMENT_BREADCRUMBS.map((breadcrumb) => ({ ...breadcrumb, isActive: location.pathname === breadcrumb.path })),
    [location.pathname],
  )

  const [initialized, setInitialized] = useState(false)
  const [isInitializing, setInitializing] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const [isSummaryLoading, setSummaryLoading] = useState(false)
  const [providers, setProviders] = useState<IProviderBase[]>([])
  const [providerCount, setProviderCount] = useState(0)
  const [pageCount, setPageCount] = useState(0)
  const [query, setQuery] = useState<ProviderListQuery>(DEFAULT_PROVIDER_LIST_QUERY)
  const [summary, setSummary] = useState<IProviderSummary | null>(null)
  const [formMeta, setFormMeta] = useState<IProviderFormMeta | null>(null)
  const [detailsMap, setDetailsMap] = useState<Record<number, IProviderDetails | null>>({})
  const [detailsLoadingMap, setDetailsLoadingMap] = useState<Record<number, boolean>>({})
  const [isProviderModalOpen, setIsProviderModalOpen] = useState(false)
  const [editingProvider, setEditingProvider] = useState<IProviderBase | null>(null)

  const initializePage = async () => {
    setInitializing(true)

    try {
      const [merchantsRes, paymentMethodsRes] = await Promise.all([
        getMerchantLookup(),
        getPaymentMethodLookup(),
      ])

      setFormMeta({
        merchants: merchantsRes?.data?.success ? merchantsRes.data.data : [],
        paymentMethods: paymentMethodsRes?.data?.success ? paymentMethodsRes.data.data : [],
      })
    } catch (error) {
      console.error('Ошибка загрузки справочников провайдеров:', error)
      setFormMeta(null)
    } finally {
      setInitializing(false)
    }
  }

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true)
    try {
      const response = await getProviderManagementSummary()
      if (!response?.data?.success) {
        throw new Error('errors' in response?.data ? response?.data?.errors?.general_error : 'Unknown error')
      }
      setSummary(response.data.data)
    } catch (error) {
      console.error('Ошибка загрузки summary:', error)
      setSummary(null)
    } finally {
      setSummaryLoading(false)
    }
  }, [])

  const loadProviderList = useCallback(async (params: ProviderListQuery) => {
    setLoading(true)
    try {
      const response = await getProviderList(params)
      if (!response?.data?.success) {
        throw new Error('errors' in response?.data ? response?.data?.errors?.general_error : 'Unknown error')
      }

      const list = response.data.data || []
      const totalRows = response.data.totalRows || 0
      const pages = Math.ceil(totalRows / params.perPage)

      setProviders(list)
      setProviderCount(totalRows)
      setPageCount(pages)
    } catch (error) {
      console.error('Ошибка загрузки провайдеров:', error)
      setProviders([])
      setProviderCount(0)
      setPageCount(0)
    } finally {
      setLoading(false)
    }
  }, [])

  const applyFilters = useCallback(({ patch, page }: ApplyProviderFiltersArgs = {}) => {
    const nextQuery: ProviderListQuery = {
      ...DEFAULT_PROVIDER_LIST_QUERY,
      ...query,
      ...patch,
      page: page ?? patch?.page ?? query.page,
    }

    setQuery(nextQuery)

    const urlQuery = serializeFiltersToUrl(nextQuery, DEFAULT_PROVIDER_LIST_QUERY)
    const nextUrl = urlQuery ? `${window.location.pathname}?${urlQuery}` : window.location.pathname
    window.history.replaceState({}, '', nextUrl)

    loadProviderList(nextQuery)
  }, [query, loadProviderList])

  const loadProviderDetails = useCallback(async (provider: IProviderBase) => {
    const providerId = provider.id
    setDetailsLoadingMap((prev) => ({ ...prev, [providerId]: true }))
    try {
      const response = await getProviderBindings(providerId)
      if (!response?.data?.success) {
        throw new Error('errors' in response?.data ? response?.data?.errors?.general_error : 'Unknown error')
      }
      const details: IProviderDetails = {
        provider,
        credentialRules: response.data.data || [],
      }
      setDetailsMap((prev) => ({ ...prev, [providerId]: details }))
      return details
    } catch (error) {
      console.error('Ошибка загрузки деталей провайдера:', error)
      setDetailsMap((prev) => ({ ...prev, [providerId]: null }))
      return null
    } finally {
      setDetailsLoadingMap((prev) => ({ ...prev, [providerId]: false }))
    }
  }, [])

  const handleOpenEditModal = useCallback(async (provider: IProviderBase) => {
    setEditingProvider(provider)
    setIsProviderModalOpen(true)
    if (!detailsMap[provider.id]) {
      await loadProviderDetails(provider)
    }
  }, [detailsMap, loadProviderDetails])

  const handleToggleProvider = useCallback(async (providerId: number) => {
    const response = await toggleProviderEnabled(providerId)
    if (!response?.data?.success) {
      console.error('Ошибка обновления статуса провайдера')
      return
    }

    const updatedProvider = response.data.data
    setProviders((prev) => prev.map((provider) => (provider.id === providerId ? updatedProvider : provider)))
    setDetailsMap((prev) => ({
      ...prev,
      [providerId]: prev[providerId] ? { ...prev[providerId]!, provider: updatedProvider } : prev[providerId],
    }))
    loadSummary()
  }, [loadSummary])

  const handleProviderSaved = useCallback((provider: IProviderBase, details?: IProviderDetails) => {
    setProviders((prev) => {
      const exists = prev.some((item) => item.id === provider.id)
      return exists ? prev.map((item) => (item.id === provider.id ? provider : item)) : [provider, ...prev]
    })

    if (details) {
      setDetailsMap((prev) => ({ ...prev, [provider.id]: details }))
    }

    loadSummary()
    applyFilters()
  }, [loadSummary, applyFilters])

  const handleBindingsChanged = useCallback((details?: IProviderDetails) => {
    if (details) {
      setDetailsMap((prev) => ({ ...prev, [details.provider.id]: details }))
    }

    loadSummary()
    applyFilters()
  }, [loadSummary, applyFilters])

  useEffect(() => {
    if (initialized) return

    const init = async () => {
      await initializePage()
      await Promise.all([loadSummary(), loadProviderList(DEFAULT_PROVIDER_LIST_QUERY)])
      setInitialized(true)
    }

    init()
  }, [initialized, loadSummary, loadProviderList])

  return (
    <div>
      <div className='card sticky-top-tickets mb-7'>
        <PageTitle breadcrumbs={breadCrumbs}>Менеджер провайдеров</PageTitle>
        <div className='card-header border-0 pt-5 pb-2'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>Управление провайдерами</span>
            <span className='text-muted mt-1 fw-semibold fs-7'>Всего провайдеров: {isLoading || isInitializing ? <span className='spinner-border spinner-border-sm align-middle'></span> : providerCount}</span>
          </h3>
          <div className='card-toolbar'>
            <button className='btn btn-sm btn-primary' disabled={isInitializing} onClick={() => { setEditingProvider(null); setIsProviderModalOpen(true) }}>
              <KTIcon iconName='plus' className='fs-2' />
              Добавить провайдера
            </button>
          </div>
        </div>
      </div>

      <ProviderSummary summary={summary} isLoading={isSummaryLoading || isInitializing} />

{isInitializing ? (
        <div className='card'>
          <div className='card-body py-10'>
            <LoaderForTable isLoading colSpan={1} />
          </div>
        </div>
      ) : (
        <ProviderList
          providers={providers}
          isLoading={isLoading}
          detailsLoadingMap={detailsLoadingMap}
          onOpenSettings={handleOpenEditModal}
          onToggle={handleToggleProvider}
        />
      )}

      {pageCount > 1 && (
        <div className='d-flex justify-content-center mt-5'>
          <ReactPaginate
            breakLabel='...'
            nextLabel='Следующая >'
            onPageChange={(selected) => applyFilters({ page: selected.selected + 1 })}
            pageRangeDisplayed={5}
            pageCount={pageCount}
            previousLabel='< Предыдущая'
            renderOnZeroPageCount={() => null}
            className='pagination'
            activeClassName='active'
            disabledClassName='disabled'
            nextClassName='page-item'
            nextLinkClassName='page-link'
            pageClassName='page-item'
            pageLinkClassName='page-link'
            previousClassName='page-item'
            previousLinkClassName='page-link'
            breakClassName='page-item'
            breakLinkClassName='page-link'
          />
        </div>
      )}

      {isProviderModalOpen && (
        <ModalProvider
          show={isProviderModalOpen}
          onClose={setIsProviderModalOpen}
          onSaved={handleProviderSaved}
          onBindingsChanged={handleBindingsChanged}
          formMeta={formMeta}
          initialProvider={editingProvider}
          initialDetails={editingProvider ? detailsMap[editingProvider.id] ?? null : null}
          canUpdateKeys={canUpdateKeys}
        />
      )}
    </div>
  )
}
